// Dono: B5 (conta e captura). /aparelhos: parear extensão/atalho (código de 6 dígitos), listar, revogar.
// mobile-shell: guia do iPhone (instalar, entrar no app instalado com código, atalho do Compartilhar).
// As chaves de API têm tela própria (/api-e-agentes, ApiPage.tsx); aqui só os aparelhos pareados.
// Esqueleto medido na página de extensões da referência (coluna de 960, cartões de raio 16, tabela de chaves); textos,
// guias e desenho nossos. Guias resumem extension/README.md e shortcuts/README-ios.md (o do iPhone já vem completo aqui).
import { useEffect, useState } from "preact/hooks";
import { type Device, LIMITS, type PairResponse } from "../../../../shared/types.ts";
import "../../styles/api.css";
import { IconTrash, Mark } from "../Icons.tsx";
import { relativeTime } from "../lib.ts";
import { IconDevice, IconPhone, IconPuzzle, IconShareSheet } from "../share/icons.tsx";
import { store, toast } from "../state.ts";
import { BackLink, Confirm, follow } from "./AccountPage.tsx";

const SCOPE: Record<string, string> = { read: "leitura", write: "escrita", private: "privados" };
const scopes = (d: Device) => d.scopes.map((s) => SCOPE[s] ?? s).join(" e ") || "nenhum";
const deviceName = (d: Device) => d.label?.trim() || "Aparelho sem nome";
const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Leva até o guia pedido em ?guia= (links da Conta) ou pelo botão do cartão de boas-vindas. */
function showGuide(id: string) {
  const el = document.getElementById(`guia-${id}`);
  el?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  el?.focus({ preventScroll: true });
}

export function DevicesPage() {
  const online = store.status.value.online;
  const [list, setList] = useState<Device[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [code, setCode] = useState<PairResponse | null>(null);
  const [now, setNow] = useState(Date.now());
  const [revoking, setRevoking] = useState<Device | null>(null);
  const [asking, setAsking] = useState(false);

  const load = () =>
    store.devices().then(
      (d) => (setList(d.filter((x) => x.kind === "device")), setFailed(false), d),
      () => (setFailed(true), null),
    );
  useEffect(() => void (online && load()), [online]);
  useEffect(() => {
    const g = new URLSearchParams(location.search).get("guia");
    if (g) requestAnimationFrame(() => showGuide(g));
    else scrollTo(0, 0);
  }, []);

  // Contagem regressiva do código; a cada 5 s confere se um aparelho nasceu depois do código (pareou → já foi usado).
  // Relógio do servidor dos dois lados (createdAt e expiresAt), sem depender da lista ter carregado antes.
  // Sem conexão não confere; e para na primeira falha (cada falha do REST vira um aviso) até a rede voltar ou outro código.
  const live = code && code.expiresAt > now;
  useEffect(() => {
    if (!live) return;
    const issued = code.expiresAt - LIMITS.pairingCodeMinutes * 60_000;
    let tick = 0;
    let stopped = !online;
    const t = setInterval(() => {
      setNow(Date.now());
      if (++tick % 5 || stopped) return;
      void load().then((d) => {
        if (!d) return void (stopped = true);
        const fresh = d.find((x) => x.kind === "device" && x.createdAt >= issued);
        if (!fresh || stopped) return; // stopped: o código foi trocado enquanto a lista vinha
        setCode(null);
        toast({ key: `paired:${fresh.id}`, text: `Pareado: ${deviceName(fresh)}.` });
      });
    }, 1000);
    return () => {
      clearInterval(t);
      stopped = true;
    };
  }, [live, code, online]);

  const pair = () =>
    store.pairDevice().then(
      (c) => (setCode(c), setNow(Date.now())),
      () => {},
    );
  const revoke = (d: Device) =>
    store.revokeDevice(d.id).then(
      () => {
        setList((l) => l?.filter((x) => x.id !== d.id) ?? null);
        toast({ key: `revoked:${d.id}`, text: `${deviceName(d)} foi desconectado. Para voltar a guardar por ele, pareie de novo.` });
      },
      () => void load(),
    );

  return (
    <main id="main" tabIndex={-1} class="page dev">
      <BackLink />
      <article class="dev-wrap">
        <h1 class="dev-title">Aparelhos e extensões</h1>

        <section class="dev-card dev-welcome" aria-labelledby="dev-welcome">
          <span class="dev-mark">
            <Mark />
          </span>
          <div class="dev-welcome-body">
            <h2 id="dev-welcome">
              Guarde de qualquer lugar <span class="acc-badge">beta</span>
            </h2>
            <p>Pareie a extensão do navegador ou o atalho do iPhone com a sua conta. Cada aparelho ganha uma chave própria, que você pode cortar aqui quando quiser.</p>
            <p>No Android não precisa parear: com o fai.g instalado na tela inicial, ele aparece na folha Compartilhar de qualquer app.</p>
            <ul class="dev-tiles">
              <li>
                <button type="button" onClick={() => showGuide("extensao")}>
                  <strong>Extensão</strong>
                  <span>Chrome e Edge, com um clique ou Alt+Shift+S.</span>
                </button>
              </li>
              <li>
                <button type="button" onClick={() => showGuide("iphone")}>
                  <strong>iPhone</strong>
                  <span>App na Tela de Início e atalho no Compartilhar.</span>
                </button>
              </li>
              <li>
                <button type="button" onClick={() => showGuide("android")}>
                  <strong>Android</strong>
                  <span>App instalado, que guarda até sem conexão.</span>
                </button>
              </li>
            </ul>
          </div>
        </section>

        <section class="dev-card dev-pair" aria-labelledby="dev-pair">
          <div class="dev-pair-text">
            <h2 id="dev-pair" class="dev-label">
              Código de pareamento
            </h2>
            <p>Vale por {LIMITS.pairingCodeMinutes} minutos e uma vez só. Gerar outro cancela o anterior.</p>
          </div>
          <button type="button" class="dev-dark" disabled={!online} onClick={pair}>
            {code ? "Gerar outro" : "Gerar código"}
          </button>
          {!online && <p class="dev-muted dev-full">Gerar código precisa de conexão.</p>}
          {code && (
            <div class={`dev-code${live ? "" : " is-expired"}`}>
              <p class="dev-digits" aria-label={`Código: ${code.code.split("").join(" ")}`}>
                {code.code.slice(0, 3)}
                <span class="dev-gap" />
                {code.code.slice(3)}
              </p>
              <p class="dev-muted" aria-hidden="true">
                {live ? `Vence em ${mmss(code.expiresAt - now)}` : "Venceu"}
              </p>
              <p class="sr-only" role="status">
                {live ? `Código gerado. Vale por ${LIMITS.pairingCodeMinutes} minutos.` : "O código venceu. Gere outro."}
              </p>
              {live && (
                <button
                  type="button"
                  class="acc-btn"
                  onClick={() =>
                    navigator.clipboard?.writeText(code.code).then(
                      () => toast({ key: "code-copy", text: "Código copiado." }),
                      () => toast({ key: "code-copy-fail", text: "Não deu para copiar." }),
                    )
                  }
                >
                  Copiar código
                </button>
              )}
            </div>
          )}
        </section>

        <section class="dev-card dev-list" aria-labelledby="dev-list">
          <h2 id="dev-list" class="dev-label">
            Seus aparelhos
          </h2>
          {list === null ? (
            <p class="dev-muted" role="status">
              {failed || !online ? "Sem conexão: a lista aparece quando a rede voltar." : "Carregando…"}
            </p>
          ) : list.length === 0 ? (
            <div class="dev-empty">
              <span class="dev-empty-icon">
                <IconPuzzle />
              </span>
              <div>
                <p class="dev-strong">Nenhum aparelho pareado</p>
                <p class="dev-muted">Gere um código acima e digite na extensão ou no atalho do iPhone.</p>
              </div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Acesso</th>
                  <th scope="col">Último uso</th>
                  <th scope="col">
                    <span class="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <th scope="row">
                      <IconDevice />
                      {deviceName(d)}
                    </th>
                    <td data-label="Acesso">{scopes(d)}</td>
                    <td data-label="Último uso">{d.lastSeenAt ? relativeTime(d.lastSeenAt) : "nunca usado"}</td>
                    <td>
                      <button
                        type="button"
                        class="dev-revoke"
                        aria-label={`Revogar ${deviceName(d)}`}
                        data-tip="Revogar"
                        onClick={() => {
                          setRevoking(d);
                          setAsking(true);
                        }}
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <Guides />

        <footer class="dev-foot api-foot">
          <a href="/conta" onClick={follow("/conta")}>
            Conta e preferências
          </a>
          <a href="/api-e-agentes" onClick={follow("/api-e-agentes")}>
            API e agentes (chaves e MCP)
          </a>
        </footer>
      </article>
      <Confirm
        id="revoke"
        open={asking}
        title={`Revogar ${revoking ? deviceName(revoking) : "aparelho"}?`}
        action="Revogar"
        onClose={() => setAsking(false)}
        onConfirm={() => revoking && void revoke(revoking)}
      >
        <p>A próxima captura feita por ele vai falhar. Para voltar a usá-lo, gere um código e pareie de novo.</p>
      </Confirm>
    </main>
  );
}

/** Uma linha para copiar (endereços dos atalhos): no celular, digitar URL longa no app Atalhos é onde se erra. */
function CopyLine({ text }: { text: string }) {
  return (
    <span class="dev-copy">
      <code>{text}</code>
      <button
        type="button"
        class="acc-btn"
        aria-label={`Copiar ${text}`}
        onClick={() =>
          navigator.clipboard?.writeText(text).then(
            () => toast({ key: "copy-url", text: "Endereço copiado." }),
            () => toast({ key: "copy-url-fail", text: "Não deu para copiar." }),
          )
        }
      >
        Copiar
      </button>
    </span>
  );
}

/**
 * Código para entrar no app instalado do iPhone (POST /v1/devices/login-code): gerado aqui, num navegador já dentro da
 * conta, e digitado em "Entrar com código" no app. Vale 10 minutos e uma vez.
 */
function LoginCode() {
  const online = store.status.value.online;
  const [code, setCode] = useState<PairResponse | null>(null);
  const [now, setNow] = useState(Date.now());
  const live = !!code && code.expiresAt > now;
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);
  const make = async () => {
    try {
      const r = await fetch("/v1/devices/login-code", { method: "POST" });
      if (!r.ok) throw new Error(String(r.status));
      setCode((await r.json()) as PairResponse);
      setNow(Date.now());
    } catch {
      toast({ key: "login-code-fail", text: "Não deu para criar o código agora. Confira a conexão e tente de novo." });
    }
  };
  return (
    <div class="dev-login-code">
      <button type="button" class="dev-dark" disabled={!online} onClick={make}>
        {code ? "Criar outro código" : "Criar código de entrada"}
      </button>
      {code && (
        <div class={`dev-code${live ? "" : " is-expired"}`}>
          <p class="dev-digits" aria-label={`Código de entrada: ${code.code.split("").join(" ")}`}>
            {code.code.slice(0, 3)}
            <span class="dev-gap" />
            {code.code.slice(3)}
          </p>
          <p class="dev-muted" aria-hidden="true">
            {live ? `Vence em ${mmss(code.expiresAt - now)}` : "Venceu"}
          </p>
          <p class="sr-only" role="status">
            {live ? `Código de entrada criado. Vale por ${LIMITS.pairingCodeMinutes} minutos.` : "O código venceu. Crie outro."}
          </p>
        </div>
      )}
    </div>
  );
}

function Guides() {
  const origin = location.origin;
  const host = location.host;
  const guide = new URLSearchParams(location.search).get("guia");
  return (
    <>
      <section id="guia-extensao" tabIndex={-1} class="dev-card dev-guide" aria-labelledby="g-ext">
        <span class="dev-guide-icon">
          <IconPuzzle />
        </span>
        <div>
          <h2 id="g-ext" class="dev-label">
            Extensão para Chrome e Edge
          </h2>
          <ol>
            <li>
              Abra <code>chrome://extensions</code> (no Edge, <code>edge://extensions</code>) e ligue o <strong>Modo do desenvolvedor</strong>.
            </li>
            <li>
              Clique em <strong>Carregar sem compactação</strong> e escolha a pasta <code>extension/</code> do projeto fai.g.
            </li>
            <li>A página de pareamento abre sozinha. Digite o código gerado acima e dê um nome ao aparelho, como “Chrome do trabalho”.</li>
            <li>Pronto: o ícone ou Alt+Shift+S guardam a página; o botão direito guarda uma imagem, um trecho ou um link.</li>
          </ol>
        </div>
      </section>
      <section id="guia-iphone" tabIndex={-1} class="dev-card dev-guide" aria-labelledby="g-ios">
        <span class="dev-guide-icon">
          <IconShareSheet />
        </span>
        <div>
          <h2 id="g-ios" class="dev-label">
            iPhone e iPad
          </h2>
          <p class="dev-muted">
            Três passos, feitos uma vez. Use sempre <strong>{host}</strong>: o app instalado guarda os dados por endereço.
          </p>
          <details open={guide === "iphone"}>
            <summary>1. Instalar na Tela de Início</summary>
            <ol>
              <li>
                Abra <strong>{origin}</strong> no Safari.
              </li>
              <li>
                Toque em <strong>Compartilhar</strong> (o quadrado com a seta) e depois em <strong>Adicionar à Tela de Início</strong>. Se aparecer <strong>Abrir como App Web</strong>,
                deixe ligado.
              </li>
              <li>
                Toque em <strong>Adicionar</strong>. O ícone do fai.g abre o app em tela cheia, sem a barra do Safari, e ele funciona até sem conexão.
              </li>
            </ol>
          </details>
          <details open={guide === "iphone"}>
            <summary>2. Entrar no app instalado</summary>
            <ol>
              <li>
                Abra o fai.g pelo ícone e toque em <strong>Entrar com Google</strong>, com a conta autorizada.
              </li>
              <li>Se o Google abrir numa folha e o app continuar na tela de entrada, feche a folha. O app confere a entrada sozinho ao voltar.</li>
              <li>
                Se ainda pedir para entrar, use um código: num navegador já dentro da conta (o Safari ou o computador), abra esta página e toque em{" "}
                <strong>Criar código de entrada</strong>. No app, toque em <strong>Entrar com código</strong> e digite os 6 números.
              </li>
              <li>
                O código abre a conta em que foi criado: confira no Menu do Safari em qual conta você está. Se for outra, o app abre essa outra, e o que ficou na fila da
                primeira espera no aparelho até você entrar nela de novo (Menu → Entrar com…).
              </li>
              <li>A sessão dura 30 dias desde o último uso. Abrir o app renova o prazo.</li>
            </ol>
            <LoginCode />
          </details>
          <details open={guide === "iphone"}>
            <summary>3. Atalho “Salvar no fai.g” no Compartilhar</summary>
            <p class="dev-muted">
              O iPhone não põe apps da Tela de Início na folha Compartilhar. Um atalho faz esse papel. São dois atalhos no app Atalhos, montados uma vez. O token fica num
              arquivo do iCloud Drive, fora do atalho que você compartilha.
            </p>
            <h3 class="dev-step">Atalho “Parear fai.g” (roda uma vez)</h3>
            <ol>
              <li>
                <strong>Pedir Entrada</strong> (Ask for Input), tipo Texto, com a pergunta “Código de 6 dígitos do fai.g”.
              </li>
              <li>
                <strong>Obter Conteúdo do URL</strong> (Get Contents of URL) com o endereço abaixo. Em Mostrar Mais: método POST e corpo JSON com <code>code</code> = Entrada
                Fornecida e <code>label</code> = <code>iPhone</code>.
                <CopyLine text={`${origin}/v1/devices/claim`} />
              </li>
              <li>
                <strong>Obter Valor do Dicionário</strong> (Get Dictionary Value), chave <code>token</code>.
              </li>
              <li>
                <strong>Salvar Arquivo</strong> (Save File): desligue Perguntar Onde Salvar, caminho <code>faig-token.txt</code>, e ligue Substituir se o Arquivo Existir.
              </li>
              <li>
                <strong>Mostrar Notificação</strong>: “Pareado com o fai.g”.
              </li>
            </ol>
            <p class="dev-muted">Para parear, toque em Gerar código (no alto desta página), rode o atalho e digite o código em até {LIMITS.pairingCodeMinutes} minutos.</p>
            <h3 class="dev-step">Atalho “Salvar no fai.g” (na folha Compartilhar)</h3>
            <ol>
              <li>
                No ⓘ do atalho, ligue <strong>Mostrar na Folha de Compartilhamento</strong>, com URLs, Páginas Web do Safari, Texto e Imagens.
              </li>
              <li>
                <strong>Obter Arquivo</strong> (Get File): pasta Atalhos, <code>faig-token.txt</code>, com Erro se Não Encontrado. Depois, <strong>Obter Texto da Entrada</strong> e
                renomeie o resultado para <code>Token</code>.
              </li>
              <li>
                <strong>Obter URLs da Entrada</strong> e <strong>Obter Item da Lista</strong> (Primeiro Item); depois <strong>Obter Imagens da Entrada</strong>.
              </li>
              <li>
                <strong>Obter Conteúdo do URL</strong> com o endereço abaixo, método POST, cabeçalho <code>Authorization</code> = <code>Bearer </code> seguido de Token, e corpo
                Formulário com <code>url</code> = Item da Lista, <code>file</code> = Imagens e <code>text</code> = Entrada do Atalho.
                <CopyLine text={`${origin}/v1/capture`} />
              </li>
              <li>
                <strong>Obter Valor do Dicionário</strong> <code>cardId</code>. Se tiver valor, <strong>Mostrar Notificação</strong> “Salvo no fai.g”; se não, mostre o{" "}
                <code>title</code> da resposta.
              </li>
            </ol>
          </details>
        </div>
      </section>
      <section id="guia-android" tabIndex={-1} class="dev-card dev-guide" aria-labelledby="g-android">
        <span class="dev-guide-icon">
          <IconPhone />
        </span>
        <div>
          <h2 id="g-android" class="dev-label">
            Android
          </h2>
          <ol>
            <li>No Chrome do celular, abra o fai.g, entre na conta e toque em ⋮ → Instalar app.</li>
            <li>Em qualquer app, toque em Compartilhar e escolha fai.g.</li>
            <li>Links, textos, fotos, vídeos, PDF e áudio viram cards. Sem conexão, eles esperam no aparelho e sobem quando a rede voltar.</li>
          </ol>
        </div>
      </section>
    </>
  );
}
